/**
 * @notes
 * - Concept taken from https://codesandbox.io/s/012ywx6mp0 and amended by Matt Rawson
 * - originally authored by Bojan Todorović <https://codesandbox.io/u/bojant987>
 */
import React, {Component} from "react"
import PropTypes from "prop-types"
import {concat, range} from "lodash"
import styled from "styled-components"
import scrollIntoView from "scroll-into-view-if-needed"

import id from "@irati/utils/id"
import theme from "@irati/ui/src/global/theme"
import connect from "@irati/store/connect"
import {Label} from "@irati/ui/src/Typography"
import {FormattedMessage} from "@irati/utils/lang"
import PaginationButton from "./PaginationButton"

const MAX_ITEMS = 7
const PAGE_SPACER = "..."

const PaginationWrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  margin-top: ${theme.spacing.two};
  padding-top: 10px;
  padding-bottom: 1px;
  border-top: 1px solid ${theme.colors.greys.light};

  > * {
    margin: 0 6px;
    &:first-child {
      margin-right: 18px;
    }
    &:last-child {
      margin-left: 18px;
    }
  }
`

class Pagination extends Component {
  static propTypes = {
    isPhone: PropTypes.bool.isRequired,
    pages: PropTypes.number,
    page: PropTypes.number,
    PageButtonComponent: PropTypes.any,
    onPageChange: PropTypes.func,
    previousText: PropTypes.string,
    nextText: PropTypes.string,
    shouldScrollOnUpdate: PropTypes.bool
  }

  static defaultProps = {
    pages: 0,
    page: 0,
    PageButtonComponent: PaginationButton,
    onPageChange: () => undefined,
    previousText: null,
    nextText: null,
    shouldScrollOnUpdate: true
  }

  constructor(props) {
    super(props)

    const {page, pages} = props

    this.state = {
      visiblePages: this.getVisiblePages(page, pages)
    }

    this.idNamespace = id("Pagination", props)
  }

  componentDidUpdate(prevProps) {
    const {pages, page, shouldScrollOnUpdate} = this.props

    /** Disabling lint because it is nested in a conditional */
    /* eslint-disable react/no-did-update-set-state */
    if (pages !== prevProps.pages) {
      this.setState({
        visiblePages: this.getVisiblePages(page, pages)
      })
    }
    /* eslint-enable react/no-did-update-set-state */

    /** when navigating via the pagination component, need to make sure it stays in the users window fold  */
    if (shouldScrollOnUpdate) {
      scrollIntoView(document.getElementById(this.idNamespace), {scrollMode: "if-needed"})
    }

    this.changePage(page + 1)
  }

  filterPages = (visiblePages, totalPages) => {
    return visiblePages.filter((page) => page <= totalPages)
  }

  getVisiblePages = (page, total) => {
    const isPageFarAwayFromStart = page > MAX_ITEMS - 3
    const reachedTheEnd = page + 2 >= total

    if (total < MAX_ITEMS) {
      return this.filterPages(range(1, MAX_ITEMS), total)
    }

    if (isPageFarAwayFromStart && !reachedTheEnd) {
      return concat(1, PAGE_SPACER, range(page - 1, page + 2), PAGE_SPACER, total)
    }

    if (isPageFarAwayFromStart && reachedTheEnd) {
      return concat(1, PAGE_SPACER, range(total - 3, total + 1))
    }

    return concat(range(1, MAX_ITEMS - 1), PAGE_SPACER, total)
  }

  changePage = (pageToChangeTo) => {
    const {page, pages, onPageChange} = this.props
    const activePage = page + 1

    if (pageToChangeTo === activePage) return

    this.setState({
      visiblePages: this.getVisiblePages(pageToChangeTo, pages)
    })

    onPageChange(pageToChangeTo - 1)
  }

  render() {
    const {PageButtonComponent, isPhone, page, previousText, nextText, pages} = this.props
    const {visiblePages} = this.state
    const activePage = page + 1
    const isPrevDisabled = activePage === 1
    const isNextDisabled = activePage === pages

    return (
      <PaginationWrapper id={this.idNamespace}>
        <PageButtonComponent
          id={id("previousBtn", this.idNamespace)}
          onClick={() => !isPrevDisabled && this.changePage(activePage - 1)}
          disabled={isPrevDisabled}>
          {previousText}
        </PageButtonComponent>

        {!isPhone &&
          visiblePages.map((pageItem, index) => {
            const pageId = pageItem === PAGE_SPACER ? `spacer_${index}` : pageItem
            return (
              <PageButtonComponent
                id={id(pageId, this.idNamespace)}
                key={pageId}
                isPlaceholder={pageItem === PAGE_SPACER}
                active={activePage === pageItem}
                onClick={() => this.changePage(pageItem)}>
                {pageItem}
              </PageButtonComponent>
            )
          })}

        {isPhone && (
          <Label>
            <FormattedMessage
              id="platform.ui.table.pagination.currentOfTotal"
              values={{
                current: activePage,
                total: visiblePages[visiblePages.length - 1]
              }}
            />
          </Label>
        )}

        <PageButtonComponent
          id={id("nextBtn", this.idNamespace)}
          onClick={() => !isNextDisabled && this.changePage(activePage + 1)}
          disabled={isNextDisabled}>
          {nextText}
        </PageButtonComponent>
      </PaginationWrapper>
    )
  }
}

const mapStateToProps = ({mq}) => ({
  isPhone: mq.lessThan.medium
})
export default connect(mapStateToProps)(Pagination)
